import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TOS_VERSION } from "./onboardingStorage";

const TOS_BODY = `
TERMS AND CONDITIONS

1. ACCEPTANCE OF TERMS
By accessing or using the FengCart service ("the Service"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree, you may not use the Service.

2. APP DEVELOPMENT AND DELIVERY
FengCart will build, configure, publish and maintain a native mobile application based on your Shopify store data, either under our own developer license or, at your discretion, under your own developer account. Delivery timelines are estimates and not contractual obligations.

3. CONTENT OWNERSHIP AND RESPONSIBILITY
You retain ownership of your store content (products, images, copy, brand assets). You represent and warrant that you have the right to use such content and that it does not infringe any third-party rights, violate any law, or contain prohibited material (including but not limited to: counterfeit goods, hate speech, harassment, sexually explicit content involving minors, illegal substances, weapons sold to non-licensed buyers, or any content that promotes violence or self-harm).

4. PROHIBITED USE
You may not use the Service to (a) violate any applicable law or regulation; (b) infringe intellectual property, privacy or publicity rights; (c) distribute malware, spam, phishing or fraudulent content; (d) misrepresent your identity, business or products; (e) bypass or attempt to bypass our security measures; (f) resell, sublicense or otherwise transfer the Service to third parties without our express written consent.

5. RIGHT TO INVESTIGATE, SUSPEND OR TERMINATE
We reserve the unilateral right, at any time and without prior notice, to: (a) review, audit and remove content that we, in our sole reasonable judgment, consider to be in violation of these Terms, applicable law, or the policies of the Apple App Store or Google Play Store; (b) suspend, restrict or terminate your access to the Service; (c) unpublish, remove or disable your application from any app store or distribution channel; (d) take any other action we deem necessary to protect our service, our users, our partners or third parties.

6. LEGAL REMEDIES
You acknowledge and agree that any breach of these Terms, including but not limited to fraudulent activity, illegal content, infringement of third-party rights, or any conduct that causes damage or reputational harm to FengCart, may result in legal action. We expressly reserve the right to pursue any and all remedies available at law or in equity, including monetary damages, injunctive relief, attorney's fees, and reporting to competent authorities. You agree to indemnify and hold FengCart harmless from any claim, loss, liability, or expense arising from your use of the Service or breach of these Terms.

7. SUBSCRIPTION AND BILLING
The Service is provided under a subscription model billed through Shopify Billing API. You will receive a free trial period as offered during onboarding. After the trial, recurring charges will apply at the rate selected. You may cancel at any time through the Shopify admin; cancellation takes effect at the end of the current billing period and does not entitle you to a refund of fees already paid.

8. SERVICE AVAILABILITY
The Service is provided on an "as-is" and "as-available" basis. We do not warrant uninterrupted or error-free operation. Scheduled maintenance, third-party outages (Shopify, AWS, app stores) and force majeure events may temporarily affect availability without liability on our part.

9. LIMITATION OF LIABILITY
To the maximum extent permitted by law, FengCart, its affiliates, officers, employees and agents shall not be liable for any indirect, incidental, special, consequential or punitive damages, including loss of profits, revenue, data or goodwill, arising from your use of or inability to use the Service. Our aggregate liability for any direct damages shall not exceed the amount paid by you to FengCart in the twelve (12) months preceding the event giving rise to the claim.

10. GOVERNING LAW AND DISPUTES
These Terms shall be governed by and construed in accordance with the laws applicable to FengCart's place of business, without regard to conflict of laws principles. Any dispute, controversy or claim arising out of or relating to these Terms shall be resolved exclusively through binding arbitration or in the competent courts of FengCart's jurisdiction, at our sole election.

11. CHANGES TO THESE TERMS
We may update these Terms from time to time. Material changes will be communicated through the Service or by email. Continued use of the Service after changes become effective constitutes acceptance of the revised Terms.

12. CONTACT
For questions regarding these Terms, contact us at legal@fengcart.com.

By checking the box and clicking "Next", you confirm that you have read, understood and accepted these Terms in full, and that you agree to be legally bound by them.
`;

const SCROLL_THRESHOLD_PX = 16;

const Step3Terms = ({ answers, updateAnswers }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) {
      return undefined;
    }

    const checkScroll = () => {
      const isAtEnd =
        node.scrollTop + node.clientHeight >= node.scrollHeight - SCROLL_THRESHOLD_PX;
      if (isAtEnd) {
        setReachedEnd(true);
      }
    };

    checkScroll();
    node.addEventListener("scroll", checkScroll);
    return () => node.removeEventListener("scroll", checkScroll);
  }, []);

  const handleCheckbox = (event) => {
    const checked = event.target.checked;
    updateAnswers({
      acceptedTos: checked,
      acceptedTosVersion: checked ? TOS_VERSION : null,
      acceptedTosAt: checked ? new Date().toISOString() : null,
    });
  };

  return (
    <div className="onboarding-step onboarding-step--terms">
      <h2 className="onboarding-step__title">
        {t("homeModalWelcome.step3.title")}
      </h2>
      <p className="onboarding-step__description">
        {t("homeModalWelcome.step3.description")}
      </p>

      <div className="onboarding-tos">
        <div ref={scrollRef} className="onboarding-tos__scroll">
          <pre className="onboarding-tos__body">{TOS_BODY.trim()}</pre>
        </div>
        {!reachedEnd && (
          <div className="onboarding-tos__hint">
            {t("homeModalWelcome.step3.scrollNotice")}
          </div>
        )}
      </div>

      <label
        className={`onboarding-checkbox ${
          !reachedEnd ? "onboarding-checkbox--disabled" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={!!answers.acceptedTos}
          onChange={handleCheckbox}
          disabled={!reachedEnd}
        />
        <span>{t("homeModalWelcome.step3.checkboxLabel")}</span>
      </label>
    </div>
  );
};

Step3Terms.canAdvance = (answers) => Boolean(answers.acceptedTos);

export default Step3Terms;
